import { useApplicationStore } from "@/store/store/applicationVMStore";
import { useTypeStore } from "@/store/store/typeStore";
import { computed, ref, Ref } from "vue";
import type { AcademicProgram, School } from "@/types/school-service";
import type { Option, FormSchema, FieldSchema } from "@/models/form";
import { $api } from "@/services/api1";
import type { SchoolVM as SchoolConfigurationVM } from "@/types/application";
import { ProductIds, ProgramTypes, LOSType } from "@/types/types";
import useFormatter from "@/use/formatter";
import * as yup from "yup";

import type { ApplicantFormVM } from "@/types/applicant";
import { Type } from "@/types/types";
import type { KeyRename } from "@/types/types";
import { NationalIdNumberTypeDefinitions } from "@/types/application-details";
import { NationalIdNumberTypeIds } from "@/types/application-details";
import useApplicationData from "./useApplicationData";
const { formatDateOfBirth } = useFormatter();

export function getOptionById(options: Option[], value?: number | string) {
  if (isNaN(Number(value))) {
    return undefined;
  }

  return options?.find((o: Option) => o.value === Number(value));
}

export function getOptionLabel(options: Option[], value?: number | string) {
  return options.find((o: Option) => o.value === Number(value))?.label;
}

export function useAccountDetailsInput(data: {
  mode: Ref<string>;
  personId: Ref<number | undefined>;
}) {
  const applicationStore = useApplicationStore();

  function isValidEmail(email?: string | null, required = true) {
    if (!required) {
      return true;
    }

    if (email !== undefined && email !== null && email?.trim() !== "") {
      const cleanedValue = email.trim().toLowerCase();
      const emails = applicationStore.Applicants.filter(
        (a) => a.id !== Number(data.personId.value) && a.emailAddress
      ).map((a) => a.emailAddress?.trim()?.toLowerCase());

      if (emails && emails.length > 0) {
        return !emails?.includes(cleanedValue);
      } else {
        return true;
      }
    } else {
      return false;
    }
  }

  return { isValidEmail };
}

export function useApplicationForm(data: {
  mode: Ref<string>;
  personId: Ref<number | undefined>;
}) {
  const applicationStore = useApplicationStore();
  const typeStore = useTypeStore();

  const { isValidEmail } = useAccountDetailsInput(data);

  const { productTypeId, programTypeId } = useApplicationData();

  const currentApplicant = computed<ApplicantFormVM | undefined>(() =>
    applicationStore.applicantsTab.find(
      (a: ApplicantFormVM) => a.id === data.personId.value
    )
  );

  const isApplicantSelected = computed<boolean>(() => {
    return applicationStore.Applicants.some((a) => a.selected);
  });

  const isStudentSelected = computed<boolean>(() => {
    return applicationStore.Students?.some((a) => a.selected);
  });

  const skipKeys = computed<string[]>(() => {
    const keys = [
      "fullName",
      "initials",
      "citizenshipStatusId",
      "academicTermId",
      "enrollmentTermId",
      "userId",
      "schoolDetailsId",
      "schoolBranchDOEId",
      "schoolDOEId",
      "academicProgramId",
      "schoolLevelTypeId",
      "dateOfBirth",
      "phoneNumberTypeId",
      "requestedAmount",
      "addressUnitTypeId",
      "addressUnitTypeName",
    ];

    if (productTypeId.value === ProductIds.LOC) {
      const keysToInclude = ["requestedAmount"];
      return keys.filter((item) => !keysToInclude.includes(item));
    }

    return keys;
  });

  const keysToRename = computed<KeyRename[]>(() => {
    return [
      { key: "academicTerm", rename: "enrollmentTerm" },
      { key: "academicProgramName", rename: "Degree Program" },
      { key: "schoolLevelTypeName", rename: "Year In School" },
      { key: "amount", rename: "requestedAmount" },
      { key: "nationalIdNumber", rename: nationalIdNumberLabel.value || "" },
      { key: "dateOfBirthDisplay", rename: "Date Of Birth" },
      { key: "phoneNumberTypeName", rename: "Phone Type" },
      { key: "phoneNumber", rename: "Preferred Phone Number" },
    ];
  });

  const citizenshipStatusOptions = computed<Option[]>(
    () => typeStore.getCitizenshipStatusOptions
  );

  const statesOptions = computed<Option[]>(() => typeStore.getStateOptions);
  const countryOptions = computed<Option[]>(() => typeStore.getCountryOptions);
  const phoneTypeOptions = computed<Option[]>(() => typeStore.getPhoneOptions);
  const schoolLevels = computed<Option[]>(() =>
    typeStore.getSchoolLevelOptions(programTypeId.value)
  );
  const prefixes = computed<string[]>(
    () => typeStore.getAddressUnitTypePrefixes
  );

  const unitOptions = computed<Option[]>(() => {
    return typeStore.getAddressUnitTypeOptions;
  });

  const addressUnitLookup = computed<LOSType[]>(
    () => typeStore.types[Type.AddressUnits]
  );

  const nationalIdNumberLabel = computed<string>(() => {
    return Number(
      applicationStore.ApplicantSelected.nationalIdNumberTypeId || 0
    ) === NationalIdNumberTypeIds.ITIN
      ? NationalIdNumberTypeDefinitions[NationalIdNumberTypeIds.ITIN]?.name
      : NationalIdNumberTypeDefinitions[NationalIdNumberTypeIds.SSN]?.name;
  });

  const validationSchema = computed(() => {
    return yup.object().shape({
      firstName: yup.string().required(),
      middleName: yup.string().notRequired(),
      lastName: yup.string().required(),
      nationalIdNumber: yup.string().required(),
      citizenshipStatusId: yup.number().required(),
      dateOfBirth: yup.date().required(),
      occupationName: yup.string().notRequired(),
      occupationField: yup.string().notRequired(),
      address: yup.object({}).required("Address is required"),
      address1: yup
        .string()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .when("address", {
          is: (value: any) => value !== null && value !== undefined,
          then: (schema) =>
            schema.required("Permanent Street Address is required"),
          otherwise: (schema) => schema.notRequired(), // Optional: defaults to the original schema
        }),

      address2: yup.string().notRequired(),
      addressUnitTypeId: yup.number().notRequired(),
      city: yup.string().when("address", {
        is: (value: any) => value !== null && value !== undefined,
        then: (schema) =>
          schema
            .required("Permanent Street Address is required")
            .max(40)
            .required("City is required"),
        otherwise: (schema) => schema.notRequired(), // Optional: defaults to the original schema
      }),
      stateId: yup
        .number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .when("address", {
          is: (value: any) => value !== null && value !== undefined,
          then: (schema) => schema.min(1).required("State is required"),
          otherwise: (schema) => schema.notRequired(), // Optional: defaults to the original schema
        }),
      postalCode: yup
        .string()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .when("address", {
          is: (value: any) => value !== null && value !== undefined,
          then: (schema) =>
            schema
              .min(1)
              .required("ZIP code is required")
              .test(
                "is-valid",
                "Invalid zip code",
                function (value, testContext) {
                  // Handles 12345, 12345-6789
                  const formatCheck1 = /^[0-9]{5}(?:-[0-9]{4})?$/.test(
                    value || ""
                  );
                  // Handles 123456789
                  const formatCheck2 = /^[0-9]{9}?$/.test(value || "");

                  if (value === undefined) {
                    return false;
                  } else if (value.length === 5 && formatCheck1) {
                    return true;
                  } else if (value.length === 10 && formatCheck1) {
                    return true;
                  } else if (value.length === 9 && formatCheck2) {
                    return true;
                  } else {
                    return false;
                  }
                }
              ),
          otherwise: (schema) => schema.notRequired(), // Optional: defaults to the original schema
        }),
      countryId: yup
        .number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .when("address", {
          is: (value: any) => value !== null && value !== undefined,
          then: (schema) => schema.required("Country is required"),
          otherwise: (schema) => schema.notRequired(), // Optional: defaults to the original schema
        }),
      phoneNumber: yup
        .string()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .required(),
      phoneNumberTypeId: yup
        .number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .required(),
      emailAddress: yup
        .string()
        .required("Email Address is required")
        .test(
          "is-valid-email",
          "Applicants must have unique email addresses. Please enter a unique email address.",
          function (value) {
            return isValidEmail(value);
          }
        ),
      userId: yup.string().notRequired(),
    });
  });

  function mapPersonToFormValues(object: any) {
    const address = object?.address;
    console.log("mapPersonToFormValues", object);
    return {
      firstName: object.firstName,
      middleName: object.middleName,
      lastName: object.lastName,
      nationalIdNumber: object.nationalIdNumber,
      citizenshipStatusId: object.citizenshipStatusId,
      citizenshipStatusName: object.citizenshipStatusName,
      dateOfBirth: object.dateOfBirth,
      dateOfBirthDisplay: formatDateOfBirth(object.dateOfBirth),
      occupation: object.occupation || object.occupationName,
      occupationField: object.occupationField,
      permanentStreetAddress: address?.address1,
      unitNumber: address?.address2,
      city: address?.city,
      state: address?.stateName,
      zipCode: address?.postalCode,
      country: address?.countryName,
      phoneNumber: object.phoneNumber,
      phoneNumberTypeId: object.phoneNumberTypeId,
      phoneNumberTypeName: object.phoneNumberTypeName,
      emailAddress: object.emailAddress,
      userId: object.userId,
      schoolName: object.schoolName,
      yearInSchool: object.yearInSchool,
      academicProgramName: object.academicProgramName,
      academicProgramId: object.academicProgramId,
      academicTerm: object.academicTerm,
      academicTermId: object.academicTermId,
      requestedAmount:
        object.requestedAmount && object.requestedAmount !== null
          ? Number(object.requestedAmount)
          : object.requestedAmount,
    };
  }

  return {
    citizenshipStatusOptions,
    currentApplicant,
    isApplicantSelected,
    isStudentSelected,
    isValidEmail,
    mapPersonToFormValues,
    statesOptions,
    countryOptions,
    phoneTypeOptions,
    productTypeId,
    programTypeId,
    schoolLevels,
    skipKeys,
    keysToRename,
    validationSchema,
    prefixes,
    unitOptions,
    addressUnitLookup,
  };
}
